import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const DAY_SHORT  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_RULES = {
  meal_prep_delivery_days : [1],
  meal_prep_cutoff_day    : 5,
  meal_prep_cutoff_time   : '23:59',
  meal_prep_min_qty       : 5,
  meal_prep_max_qty       : 20,
  meal_prep_daily_limit   : 10,
  corporate_delivery_days : [1, 2, 3, 4, 5],
  corporate_cutoff_day    : 4,
  corporate_cutoff_time   : '17:00',
  meet_up_min_qty         : 5,
  meet_up_discount_pct    : 0,
};

// ── Day-of-week pill picker ────────────────────────────────────────────────
function DayPicker({ label, value, onChange }) {
  const toggle = (day) => {
    if (value.includes(day)) {
      if (value.length === 1) return; // always keep at least one day
      onChange(value.filter(d => d !== day).sort((a, b) => a - b));
    } else {
      onChange([...value, day].sort((a, b) => a - b));
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2 flex-wrap">
        {DAY_SHORT.map((day, i) => (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            className={`w-11 h-11 rounded-full text-xs font-bold transition-all ${
              value.includes(i)
                ? 'bg-blush-300 text-white shadow-sm'
                : 'bg-sage-100 text-sage-500 hover:bg-sage-200'
            }`}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── +/− number stepper ─────────────────────────────────────────────────────
function NumberStepper({ label, desc, value, min = 1, max, onChange }) {
  return (
    <div className="space-y-2">
      <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">{label}</label>
      {desc && <p className="text-xs text-sage-400">{desc}</p>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
        >−</button>
        <span className="text-3xl font-serif text-charcoal w-10 text-center">{value}</span>
        <button
          type="button"
          onClick={() => onChange(max ? Math.min(max, value + 1) : value + 1)}
          className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
        >+</button>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────
export default function OrderRulesPanel() {
  const [rules, setRules]       = useState(DEFAULT_RULES);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => { fetchRules(); }, []);

  async function fetchRules() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      try {
        const { data, error } = await supabase
          .from('delivery_settings')
          .select([
            'meal_prep_delivery_days', 'meal_prep_cutoff_day', 'meal_prep_cutoff_time',
            'meal_prep_min_qty', 'meal_prep_max_qty', 'meal_prep_daily_limit',
            'corporate_delivery_days', 'corporate_cutoff_day', 'corporate_cutoff_time',
          ].join(','))
          .eq('id', 1)
          .single();
        if (error) throw error;
        if (data) {
          setRules({
            meal_prep_delivery_days : data.meal_prep_delivery_days ?? DEFAULT_RULES.meal_prep_delivery_days,
            meal_prep_cutoff_day    : data.meal_prep_cutoff_day    ?? DEFAULT_RULES.meal_prep_cutoff_day,
            meal_prep_cutoff_time   : data.meal_prep_cutoff_time   ? data.meal_prep_cutoff_time.slice(0, 5) : DEFAULT_RULES.meal_prep_cutoff_time,
            meal_prep_min_qty       : data.meal_prep_min_qty       ?? DEFAULT_RULES.meal_prep_min_qty,
            meal_prep_max_qty       : data.meal_prep_max_qty       ?? DEFAULT_RULES.meal_prep_max_qty,
            meal_prep_daily_limit   : data.meal_prep_daily_limit   ?? DEFAULT_RULES.meal_prep_daily_limit,
            corporate_delivery_days : data.corporate_delivery_days ?? DEFAULT_RULES.corporate_delivery_days,
            corporate_cutoff_day    : data.corporate_cutoff_day    ?? DEFAULT_RULES.corporate_cutoff_day,
            corporate_cutoff_time   : data.corporate_cutoff_time   ? data.corporate_cutoff_time.slice(0, 5) : DEFAULT_RULES.corporate_cutoff_time,
            meet_up_min_qty         : data.meet_up_min_qty         ?? DEFAULT_RULES.meet_up_min_qty,
            meet_up_discount_pct    : data.meet_up_discount_pct    ?? DEFAULT_RULES.meet_up_discount_pct,
          });
        }
      } catch (err) {
        console.error('Failed to load order rules:', err);
      }
    }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setSaveError('');
    if (isSupabaseConfigured()) {
      try {
        const { error } = await supabase
          .from('delivery_settings')
          .update({
            meal_prep_delivery_days : rules.meal_prep_delivery_days,
            meal_prep_cutoff_day    : rules.meal_prep_cutoff_day,
            meal_prep_cutoff_time   : rules.meal_prep_cutoff_time + ':00',
            meal_prep_min_qty       : rules.meal_prep_min_qty,
            meal_prep_max_qty       : rules.meal_prep_max_qty,
            meal_prep_daily_limit   : rules.meal_prep_daily_limit,
            corporate_delivery_days : rules.corporate_delivery_days,
            corporate_cutoff_day    : rules.corporate_cutoff_day,
            corporate_cutoff_time   : rules.corporate_cutoff_time + ':00',
            meet_up_min_qty         : rules.meet_up_min_qty,
            meet_up_discount_pct    : rules.meet_up_discount_pct,
            updated_at              : new Date().toISOString(),
          })
          .eq('id', 1);
        if (error) throw error;
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } catch (err) {
        console.error('Failed to save order rules:', err);
        setSaveError('Save failed. Check that your Supabase RLS policy allows admin UPDATE on delivery_settings.');
      }
    } else {
      // Mock mode — just show saved feedback
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  }

  const set = (key, val) => setRules(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-2xl">

      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif text-charcoal">Order Rules</h2>
        <p className="text-sage-500 text-sm mt-1">
          Control delivery days, order cutoffs, and quantity limits for meal prep and corporate orders.
        </p>
      </div>

      {/* ── Meal Prep Rules ── */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-8 shadow-soft">
        <div className="flex items-center gap-3 border-b border-sage-50 pb-5">
          <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center shrink-0">
            <SafeIcon name="UtensilsCrossed" className="text-green-500" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-charcoal">Meal Prep Rules</h3>
            <p className="text-sage-400 text-xs font-medium">Weekly meal prep scheduling & limits</p>
          </div>
        </div>

        <DayPicker
          label="Delivery Days"
          value={rules.meal_prep_delivery_days}
          onChange={v => set('meal_prep_delivery_days', v)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">Order Cutoff Day</label>
            <p className="text-xs text-sage-400">Last day customers can order for that week's delivery.</p>
            <select
              className="input-field w-full"
              value={rules.meal_prep_cutoff_day}
              onChange={e => set('meal_prep_cutoff_day', parseInt(e.target.value))}
            >
              {DAY_FULL.map((day, i) => (
                <option key={i} value={i}>{day}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">Cutoff Time</label>
            <p className="text-xs text-sage-400">Time on the cutoff day when ordering closes.</p>
            <input
              type="time"
              className="input-field w-full"
              value={rules.meal_prep_cutoff_time}
              onChange={e => set('meal_prep_cutoff_time', e.target.value)}
            />
          </div>
        </div>

        {/* Summary sentence */}
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-xs text-sage-600 font-medium flex items-start gap-2">
          <SafeIcon name="Info" className="text-green-500 shrink-0 mt-0.5" />
          <span>
            Customers order by{' '}
            <strong className="text-charcoal">{DAY_FULL[rules.meal_prep_cutoff_day]} at {rules.meal_prep_cutoff_time}</strong>.
            You prep on the weekend and deliver on{' '}
            <strong className="text-charcoal">{rules.meal_prep_delivery_days.map(d => DAY_FULL[d]).join(', ')}</strong>.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-sage-50 pt-6">
          <NumberStepper
            label="Min Portions"
            desc="Minimum dishes per order"
            value={rules.meal_prep_min_qty}
            min={1}
            max={rules.meal_prep_max_qty}
            onChange={v => set('meal_prep_min_qty', v)}
          />
          <NumberStepper
            label="Max Portions"
            desc="Maximum dishes per order"
            value={rules.meal_prep_max_qty}
            min={rules.meal_prep_min_qty}
            onChange={v => set('meal_prep_max_qty', v)}
          />
          <NumberStepper
            label="Weekly Cap"
            desc="Max meal prep orders per delivery day"
            value={rules.meal_prep_daily_limit}
            min={1}
            onChange={v => set('meal_prep_daily_limit', v)}
          />
        </div>
      </div>

      {/* ── Corporate Rules ── */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-8 shadow-soft">
        <div className="flex items-center gap-3 border-b border-sage-50 pb-5">
          <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
            <SafeIcon name="Building2" className="text-blue-400" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-charcoal">Corporate Rules</h3>
            <p className="text-sage-400 text-xs font-medium">Office & group order scheduling</p>
          </div>
        </div>

        <DayPicker
          label="Delivery Days"
          value={rules.corporate_delivery_days}
          onChange={v => set('corporate_delivery_days', v)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">Order Cutoff Day</label>
            <p className="text-xs text-sage-400">Last day coordinators can submit orders for that week.</p>
            <select
              className="input-field w-full"
              value={rules.corporate_cutoff_day}
              onChange={e => set('corporate_cutoff_day', parseInt(e.target.value))}
            >
              {DAY_FULL.map((day, i) => (
                <option key={i} value={i}>{day}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-sage-500 uppercase tracking-wider">Cutoff Time</label>
            <p className="text-xs text-sage-400">Time on the cutoff day when ordering closes.</p>
            <input
              type="time"
              className="input-field w-full"
              value={rules.corporate_cutoff_time}
              onChange={e => set('corporate_cutoff_time', e.target.value)}
            />
          </div>
        </div>

        {/* Summary sentence */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-sage-600 font-medium flex items-start gap-2">
          <SafeIcon name="Info" className="text-blue-400 shrink-0 mt-0.5" />
          <span>
            Corporate orders close on{' '}
            <strong className="text-charcoal">{DAY_FULL[rules.corporate_cutoff_day]} at {rules.corporate_cutoff_time}</strong>.
            Deliveries run on{' '}
            <strong className="text-charcoal">{rules.corporate_delivery_days.map(d => DAY_FULL[d]).join(', ')}</strong>.
          </span>
        </div>
      </div>

      {/* ── Meet-Up Rules ── */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-8 shadow-soft">
        <div className="flex items-center gap-3 border-b border-sage-50 pb-5">
          <div className="w-10 h-10 bg-blush-50 rounded-full flex items-center justify-center shrink-0">
            <SafeIcon name="MapPin" className="text-blush-400" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-charcoal">Meet-Up Rules</h3>
            <p className="text-sage-400 text-xs font-medium">Sunday night pick-up event settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NumberStepper
            label="Min Portions"
            desc="Minimum portions per meet-up order"
            value={rules.meet_up_min_qty}
            min={1}
            max={50}
            onChange={v => set('meet_up_min_qty', v)}
          />
          <NumberStepper
            label="Discount %"
            desc="Optional discount applied to meet-up orders (0 = no discount)"
            value={rules.meet_up_discount_pct}
            min={0}
            max={50}
            onChange={v => set('meet_up_discount_pct', v)}
          />
        </div>

        <div className="bg-blush-50 border border-blush-100 rounded-xl p-4 text-xs text-sage-600 font-medium flex items-start gap-2">
          <SafeIcon name="Info" className="text-blush-400 shrink-0 mt-0.5" />
          <span>
            Meet-up orders require a minimum of{' '}
            <strong className="text-charcoal">{rules.meet_up_min_qty} portion{rules.meet_up_min_qty === 1 ? '' : 's'}</strong>.
            {rules.meet_up_discount_pct > 0
              ? <> Customers get <strong className="text-charcoal">{rules.meet_up_discount_pct}% off</strong> their order subtotal.</>
              : ' No discount is currently applied.'}
          </span>
        </div>
      </div>

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-red-600 text-xs font-bold">{saveError}</p>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {saving ? (
          <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
        ) : saved ? (
          <><SafeIcon name="Check" /> Saved!</>
        ) : (
          <><SafeIcon name="Save" /> Save Order Rules</>
        )}
      </button>
    </div>
  );
}
